/**
 * CP-481: editor de `transfer_to_account` (widget "account" del catálogo CONFIGKEYS)
 * en 4 campos, como webadmin1. El valor guardado es `banco:cuenta:id_fiscal:nombre`:
 *   AR: "150:1500053700011064628596:20-23782361-1:Juan Perez"   (banco 3, CBU 22, CUIT NN-NNNNNNNN-N)
 *   MX: "40030:030760900003016647:CPM140519JV0:CROL PFF MEXICO" (banco 5, CLABE 18, RFC)
 * Las reglas por campo son las mismas partes de la regex `validation` del catálogo
 * (que sigue siendo la que habilita Save); acá solo sirven para marcar QUÉ campo falla.
 *
 * Sin imports de runtime para que corra con `node --test`.
 */

export interface AccountParts { bank: string; account: string; taxId: string; name: string }
export type AccountField = keyof AccountParts;
export type AccountCountry = "AR" | "MX";

/** Separa por ":"; el nombre se queda con el resto (por si trae ":"). */
export function splitAccount(value: string): AccountParts {
  const p = (value ?? "").split(":");
  return { bank: p[0] ?? "", account: p[1] ?? "", taxId: p[2] ?? "", name: p.slice(3).join(":") };
}

/** Une los 4 campos. Todo vacío → "" (no "::::"), para que un alta vacía siga vacía. */
export function joinAccount(p: AccountParts): string {
  const parts = [p.bank, p.account, p.taxId, p.name].map((s) => s.trim());
  return parts.every((s) => !s) ? "" : parts.join(":");
}

const RULES: Record<AccountCountry, Record<AccountField, { re: RegExp; msg: string }>> = {
  AR: {
    bank:    { re: /^\d{3}$/,               msg: "3 digits" },
    account: { re: /^\d{22}$/,              msg: "CBU: 22 digits" },
    taxId:   { re: /^\d{2}-\d{8}-\d$/,      msg: "CUIT: NN-NNNNNNNN-N" },
    name:    { re: /^[\w\s.]+$/,            msg: "Letters without accents, numbers, spaces and dots" },
  },
  MX: {
    bank:    { re: /^\d{5}$/,               msg: "5 digits" },
    account: { re: /^\d{18}$/,              msg: "CLABE: 18 digits" },
    taxId:   { re: /^\w+$/,                 msg: "RFC: letters and numbers" },
    name:    { re: /^[\w\s.]+$/,            msg: "Letters without accents, numbers, spaces and dots" },
  },
};

export const ACCOUNT_LABELS: Record<AccountCountry, Record<AccountField, string>> = {
  AR: { bank: "Bank code", account: "CBU", taxId: "CUIT", name: "Name" },
  MX: { bank: "Bank code", account: "CLABE", taxId: "RFC", name: "Name" },
};

export const accountCountry = (country: string): AccountCountry => (country.toUpperCase() === "MX" ? "MX" : "AR");

/** Error por campo (null = ok). Un campo vacío no se marca: lo frena la validación general. */
export function accountFieldError(country: AccountCountry, field: AccountField, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  const r = RULES[country][field];
  return r.re.test(v) ? null : r.msg;
}
